import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Check,
  Monitor,
  Moon,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { useTheme } from '@/app/providers';
import {
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
// fork(oem): OEM identity selector, modelled on the DiffThemeCard group below
import { useBrand, type Brand } from '@/versions/v1/shared/BrandContext';
// fork(oem): executive / full rail selector, same card group again
import {
  DEMO_MODE_OPTIONS,
  useDemoMode,
  type DemoMode,
} from '@/versions/v1/shared/DemoModeContext';

type ThemeChoiceValue = 'light' | 'dark' | 'system';

type ThemeChoice = {
  value: ThemeChoiceValue;
  label: string;
  description: string;
  Icon: LucideIcon;
};

const choices: ThemeChoice[] = [
  {
    value: 'light',
    label: 'Light',
    description: 'Bright surfaces, dark text.',
    Icon: Sun,
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Dim surfaces, light text.',
    Icon: Moon,
  },
  {
    value: 'system',
    label: 'System',
    description: 'Match your OS setting.',
    Icon: Monitor,
  },
];

type DiffTheme = 'default' | 'colorblind' | 'cursor';

const DIFF_THEME_OPTIONS: DiffTheme[] = ['default', 'colorblind', 'cursor'];

const DIFF_THEME_LABELS: Record<DiffTheme, string> = {
  default: 'Default',
  colorblind: 'Colorblind-safe',
  cursor: 'High contrast',
};

const DIFF_THEME_DESCRIPTIONS: Record<DiffTheme, string> = {
  default: 'Green insert, rose delete.',
  colorblind: 'Blue insert, amber delete.',
  cursor: 'Emerald insert, red delete.',
};

export function AppearanceScene() {
  const { theme, setTheme } = useTheme();
  const [themeChoice, setThemeChoice] = useState<ThemeChoiceValue>('light');
  const [systemNote, setSystemNote] = useState(false);
  const [diffTheme, setDiffTheme] = useState<DiffTheme>('default');
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  // fork(oem): deep links such as /settings/appearance#oem-identity (the
  // Settings sidebar row) scroll their section into view. React Router does
  // not do this for in-app navigation.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [hash]);

  useEffect(() => {
    if (!mounted) return;
    if (themeChoice !== 'system') {
      setThemeChoice(theme);
    }
  }, [mounted, theme, themeChoice]);

  const handleThemeSelect = (value: ThemeChoiceValue) => {
    setThemeChoice(value);
    if (value === 'system') {
      setTheme('light');
      setSystemNote(true);
    } else {
      setTheme(value);
      setSystemNote(false);
    }
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="AppearanceScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
          <SettingsPageHeader
            title="Appearance"
            description="Choose how the interface looks."
          />

          <Commentable name="ThemeCards">
            <div>
              {systemNote ? (
                <p className="mb-3 text-sm text-muted">
                  System preference is not wired in this proof of concept — applied Light
                  theme.
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {choices.map((choice) => (
                  <ThemeCard
                    key={choice.value}
                    choice={choice}
                    active={mounted && themeChoice === choice.value}
                    onSelect={() => handleThemeSelect(choice.value)}
                  />
                ))}
              </div>
            </div>
          </Commentable>

          <Commentable name="DiffThemeCards">
            <section className="flex flex-col gap-4">
              <div>
                <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                  Diff theme
                </h2>
                <p className="mt-1 text-sm text-muted">
                  Colors for insert and delete highlights in the document editor.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {DIFF_THEME_OPTIONS.map((preset) => (
                  <DiffThemeCard
                    key={preset}
                    preset={preset}
                    active={mounted && diffTheme === preset}
                    onSelect={() => setDiffTheme(preset)}
                  />
                ))}
              </div>
            </section>
          </Commentable>

          {/* fork(oem): OEM identity cards (anchor name kept from "Client") */}
          <Commentable name="ClientBrandCards">
            <ClientBrandCards />
          </Commentable>

          {/* fork(oem/P7a A6): how much of the build the left rail admits */}
          <Commentable name="DemoModeCards">
            <DemoModeCards />
          </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

// fork(oem) — everything below this line.

/**
 * OEM identity selector. The same card group as the diff-theme cards above
 * (button + aria-pressed + preview swatch), so the settings page reads as one
 * surface rather than a foundation page with a product bolt-on.
 *
 * Meridian (fictional, pink machines) is the default and the safe choice for
 * any audience; Komatsu (yellow machines) is for Komatsu-only sessions. The
 * section id is the target of the Settings sidebar's "OEM identity" row.
 */
function ClientBrandCards() {
  const { brand, brands, setBrand } = useBrand();

  return (
    <section id="oem-identity" className="flex scroll-mt-6 flex-col gap-4">
      <div>
        <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
          OEM identity
        </h2>
        <p className="mt-1 text-sm text-muted">
          Meridian Equipment is a fictional OEM — safe for any audience; its
          names and data are anonymised. Switch to Komatsu only for Komatsu-only
          sessions. The identity skins the lockup, accent, charts, favicon and
          machine paint; fleet data, mines and personas are identical under both.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {brands.map((option) => (
          <ClientBrandCard
            key={option.id}
            brand={option}
            active={option.id === brand.id}
            onSelect={() => setBrand(option.id)}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Demo-mode selector — simplification pass A6.
 *
 * The same card group as the brand and diff-theme cards above, for the same
 * reason: the settings page should read as one surface rather than a
 * foundation page with product bolt-ons. It exists so a presenter can show
 * the whole build mid-demo without a rebuild, a redeploy or a URL nobody has
 * memorised.
 */
function DemoModeCards() {
  const { mode, setMode } = useDemoMode();

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
          Demo mode
        </h2>
        <p className="mt-1 text-sm text-muted">
          How much of the build the left rail admits. Nothing is switched off:
          every surface stays reachable by URL, by ⌘K and from the links on the
          pages themselves.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {DEMO_MODE_OPTIONS.map((option) => (
          <DemoModeCard
            key={option.id}
            id={option.id}
            label={option.label}
            description={option.description}
            active={option.id === mode}
            onSelect={() => setMode(option.id)}
          />
        ))}
      </div>
    </section>
  );
}

function DemoModeCard({
  id,
  label,
  description,
  active,
  onSelect,
}: {
  id: DemoMode;
  label: string;
  description: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        active
          ? 'group flex flex-col gap-3 rounded-2xl border-2 border-accent-500 bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors'
          : 'group flex flex-col gap-3 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors hover:border-[var(--glass-border-strong)]'
      }
    >
      <DemoModePreview mode={id} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-zinc-900 dark:text-zinc-100">
            {label}
          </span>
          {active ? (
            <Check className="h-3.5 w-3.5 text-zinc-700 dark:text-zinc-200" />
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-muted">{description}</p>
      </div>
    </button>
  );
}

/**
 * A rail, at a glance: one bar per section heading, one thin bar per row that
 * is open on first paint.
 *
 * The numbers are the real ones — the executive allowlist is 7 · 3 · 6 with
 * Mine sites collapsed, the full nav is 8 · 3 · 37 · 5 · 9 · 6 · 3 · 3 with
 * five of the eight sections open. So the Executive card is thirteen bars
 * with room underneath and the Full card runs off the bottom of its box. The
 * overflow is the argument the card is making.
 */
function DemoModePreview({ mode }: { mode: DemoMode }) {
  const sections: readonly { rows: number; open: boolean }[] =
    mode === 'executive'
      ? [
          { rows: 7, open: true },
          { rows: 3, open: true },
          { rows: 6, open: false },
        ]
      : [
          { rows: 8, open: true },
          { rows: 3, open: true },
          { rows: 37, open: true },
          { rows: 5, open: false },
          { rows: 9, open: true },
          { rows: 6, open: false },
          { rows: 3, open: true },
          { rows: 3, open: false },
        ];

  return (
    <div
      className="h-24 overflow-hidden rounded-lg border border-divider bg-zinc-50 p-2 dark:bg-zinc-900"
      aria-hidden="true"
    >
      <div className="flex flex-col gap-[3px]">
        {sections.map((section, index) => (
          <div key={index} className="flex flex-col gap-[3px]">
            <div
              className="h-[3px] w-8 rounded-full"
              style={{ background: 'var(--accent-soft)' }}
            />
            {section.open
              ? Array.from({ length: section.rows }, (_, row) => (
                  <div
                    key={row}
                    className="h-[2px] rounded-full bg-zinc-200 dark:bg-zinc-700"
                    style={{ width: `${58 + ((row * 13) % 34)}%` }}
                  />
                ))
              : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ClientBrandCard({
  brand,
  active,
  onSelect,
}: {
  brand: Brand;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        active
          ? 'group flex flex-col gap-3 rounded-2xl border-2 border-accent-500 bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors'
          : 'group flex flex-col gap-3 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors hover:border-[var(--glass-border-strong)]'
      }
    >
      <div className="flex h-24 items-center justify-center rounded-lg border border-divider bg-zinc-50 p-4 dark:bg-zinc-900">
        <img
          src={brand.logo}
          alt=""
          aria-hidden="true"
          className="max-h-12 w-auto max-w-full dark:hidden"
        />
        <img
          src={brand.logoReversed}
          alt=""
          aria-hidden="true"
          className="hidden max-h-12 w-auto max-w-full dark:block"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-3 w-3 flex-shrink-0 rounded-full"
            style={{ background: brand.accentHex }}
          />
          <span className="font-medium text-zinc-900 dark:text-zinc-100">
            {brand.lockup}
          </span>
          {active ? (
            <Check className="h-3.5 w-3.5 text-zinc-700 dark:text-zinc-200" />
          ) : null}
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted">
            <span
              aria-hidden="true"
              className="h-3 w-5 flex-shrink-0 rounded-sm"
              style={{
                background: brand.machineHex,
                boxShadow: 'inset 0 0 0 1px var(--hairline-strong)',
              }}
            />
            Machines
          </span>
        </div>
        <p className="mt-0.5 text-xs text-muted">{brand.trademark}</p>
      </div>
    </button>
  );
}

function DiffThemeCard({
  preset,
  active,
  onSelect,
}: {
  preset: DiffTheme;
  active: boolean;
  onSelect: () => void;
}) {
  const label = DIFF_THEME_LABELS[preset];
  const description = DIFF_THEME_DESCRIPTIONS[preset];

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        active
          ? 'group flex flex-col gap-3 rounded-2xl border-2 border-accent-500 bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors'
          : 'group flex flex-col gap-3 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors hover:border-[var(--glass-border-strong)]'
      }
    >
      <DiffThemePreview preset={preset} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-zinc-900 dark:text-zinc-100">
            {label}
          </span>
          {active ? (
            <Check className="h-3.5 w-3.5 text-zinc-700 dark:text-zinc-200" />
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-muted">{description}</p>
      </div>
    </button>
  );
}

function DiffThemePreview({ preset }: { preset: DiffTheme }) {
  const insert =
    preset === 'colorblind'
      ? 'bg-sky-200'
      : preset === 'cursor'
        ? 'bg-emerald-200'
        : 'bg-emerald-100';
  const del =
    preset === 'colorblind'
      ? 'bg-amber-200 line-through'
      : preset === 'cursor'
        ? 'bg-red-200 line-through'
        : 'bg-rose-100 line-through';

  return (
    <div className="flex h-24 flex-col justify-center gap-1.5 rounded-lg border border-divider bg-zinc-50 p-3 dark:bg-zinc-900">
      <div className={cn('rounded px-2 py-0.5 text-xs text-zinc-800', insert)}>
        inserted text
      </div>
      <div className={cn('rounded px-2 py-0.5 text-xs text-zinc-800', del)}>
        deleted text
      </div>
    </div>
  );
}

function ThemeCard({
  choice,
  active,
  onSelect,
}: {
  choice: ThemeChoice;
  active: boolean;
  onSelect: () => void;
}) {
  const { value, label, description, Icon } = choice;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        active
          ? 'group flex flex-col gap-3 rounded-2xl border-2 border-accent-500 bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors'
          : 'group flex flex-col gap-3 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors hover:border-[var(--glass-border-strong)]'
      }
    >
      <ThemePreview variant={value} />
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-zinc-900 dark:text-zinc-100">
              {label}
            </span>
            {active ? (
              <Check className="h-3.5 w-3.5 text-zinc-700 dark:text-zinc-200" />
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-muted">{description}</p>
        </div>
      </div>
    </button>
  );
}

function ThemePreview({ variant }: { variant: ThemeChoiceValue }) {
  if (variant === 'system') {
    return (
      <div className="relative h-24 overflow-hidden rounded-lg border border-divider">
        <div className="absolute inset-0 grid grid-cols-2">
          <MiniMock tone="light" />
          <MiniMock tone="dark" />
        </div>
      </div>
    );
  }
  return (
    <div className="h-24 overflow-hidden rounded-lg border border-divider">
      <MiniMock tone={variant} />
    </div>
  );
}

function MiniMock({ tone }: { tone: 'light' | 'dark' }) {
  const isDark = tone === 'dark';
  const bg = isDark ? 'bg-zinc-900' : 'bg-zinc-50';
  const card = isDark ? 'bg-zinc-800' : 'bg-white';
  const line = isDark ? 'bg-zinc-700' : 'bg-zinc-200';
  const accent = isDark ? 'bg-zinc-600' : 'bg-zinc-300';

  return (
    <div className={cn('flex h-full w-full flex-col gap-1.5 p-2', bg)}>
      <div className={cn('h-2 w-1/2 rounded-full', accent)} />
      <div className={cn('flex-1 rounded-md p-1.5', card)}>
        <div className={cn('h-1.5 w-3/4 rounded-full', line)} />
        <div className={cn('mt-1 h-1.5 w-1/2 rounded-full', line)} />
      </div>
    </div>
  );
}
